import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { keycloak, initKeycloak } from "./kc";
import { buildArticlesUrl, DEFAULT_LOOKBACK_MS, AUTO_REFRESH_MS } from "./envconfigs";

export default function App() {
  // ------------ Auth / data state ------------
  const [authenticated, setAuthenticated] = useState(false);
  const [profile, setProfile] = useState(null);
  const [token, setToken] = useState(null); // optional, for visibility/debug
  const [rows, setRows] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // timers
  const refreshTimerRef = useRef(null);
  const tokenRefreshTimerRef = useRef(null);

  // manual fetch UI
  const [manualDt, setManualDt] = useState("");

  // ------------ Display shaping ------------
  // Hide these fields entirely
  const HIDDEN_FIELDS = new Set(["provider", "category", "source", "link"]);
  // Whether to display "lat, lon" (true) vs "lon, lat" (false)
  const COORDS_AS_LAT_LON = true;

  function extractCoordinates(areas) {
    try {
      if (!Array.isArray(areas)) return "";
      const parts = [];
      for (const a of areas) {
        const coords = a?.point?.coordinates;
        if (Array.isArray(coords) && coords.length >= 2) {
          const [lon, lat] = coords;
          const latStr = typeof lat === "number" ? lat.toFixed(6) : String(lat);
          const lonStr = typeof lon === "number" ? lon.toFixed(6) : String(lon);
          parts.push(COORDS_AS_LAT_LON ? `${latStr}, ${lonStr}` : `${lonStr}, ${latStr}`);
        }
      }
      return parts.join(" | ");
    } catch {
      return "";
    }
  }

  // Build a filtered+flattened view for the table/CSV
  const displayRows = useMemo(() => {
    return rows.map((row) => {
      const out = {};
      for (const [k, v] of Object.entries(row || {})) {
        if (HIDDEN_FIELDS.has(k)) continue;
        out[k] = k === "areas" ? extractCoordinates(v) : v;
      }
      return out;
    });
  }, [rows]);

  // Preferred column order (any other keys get appended at the end)
  const tableHeaders = useMemo(() => {
    if (!displayRows.length) return [];
    const keys = new Set();
    displayRows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
    const ORDER = ["publishedAt", "severity", "title", "summary", "content", "id", "messageId", "areas"];
    return [...ORDER.filter((k) => keys.has(k)), ...[...keys].filter((k) => !ORDER.includes(k))];
  }, [displayRows]);

  // ------------ UI helpers ------------
  const nowMinusDefault = useMemo(() => {
    const d = new Date(Date.now() - DEFAULT_LOOKBACK_MS);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  useEffect(() => {
    if (!manualDt) setManualDt(nowMinusDefault);
  }, [nowMinusDefault, manualDt]);

  function formatDateTime(val) {
    try {
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val ?? "");
      return d.toLocaleString();
    } catch {
      return String(val ?? "");
    }
  }

  function SeverityPill({ value }) {
    const v = String(value || "").toLowerCase();
    const map = {
      critical: "bg-red-50 text-red-700 border-red-200",
      high: "bg-orange-50 text-orange-700 border-orange-200",
      medium: "bg-amber-50 text-amber-700 border-amber-200",
      low: "bg-emerald-50 text-emerald-700 border-emerald-200",
      info: "bg-slate-100 text-slate-700 border-slate-200",
    };
    const cls = map[v] || "bg-slate-100 text-slate-700 border-slate-200";
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
        {value}
      </span>
    );
  }

  function formatCell(val, key) {
    if (key === "publishedAt") {
      return <span title={String(val)} className="whitespace-nowrap">{formatDateTime(val)}</span>;
    }
    if (key === "severity") {
      return <SeverityPill value={val} />;
    }
    if (key === "id" || key === "messageId") {
      return <span className="font-mono text-xs break-all">{String(val ?? "")}</span>;
    }
    if (key === "areas") {
      const parts = typeof val === "string" ? val.split(" | ").filter(Boolean) : [];
      return (
        <div className="flex flex-wrap gap-1">
          {parts.map((p, i) => (
            <span
              key={i}
              className="inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs"
            >
              {p}
            </span>
          ))}
        </div>
      );
    }
    if (key === "summary" || key === "content") {
      const s = String(val ?? "");
      if (s.length <= 200) return s;
      const preview = s.slice(0, 200);
      return (
        <details>
          <summary className="cursor-pointer select-none text-slate-700">{preview}…</summary>
          <div className="mt-2 text-slate-700">{s}</div>
        </details>
      );
    }
    if (val == null) return "";
    if (typeof val === "object") {
      try {
        return <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(val, null, 2)}</pre>;
      } catch {
        return String(val);
      }
    }
    const s = String(val);
    return s.length > 400 ? <span title={s}>{s.slice(0, 400)}…</span> : s;
  }

  // ------------ Fetch (stable; reads live KC token) ------------
  const fetchArticles = useCallback(async (startMs, { prepend = true } = {}) => {
    const jwt = keycloak.token || token;
    if (!jwt) {
      console.log("[fetch] skipped: no token yet");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const url = buildArticlesUrl(startMs); // preserves ':' in timestamp
      console.log("[fetch] GET", url);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${jwt}`, Accept: "application/json" },
      });

      if (res.status === 200) {
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error("API response is not an array.");
        setRows((prev) => {
          const seen = new Set(prev.map((r) => JSON.stringify(r)));
          const fresh = [];
          for (const item of data) {
            const k = JSON.stringify(item);
            if (!seen.has(k)) {
              fresh.push(item);
              seen.add(k);
            }
          }
          return prepend ? [...fresh, ...prev] : [...prev, ...fresh];
        });
      } else if (res.status === 204) {
        // no content
      } else if (res.status === 401) {
        try {
          await keycloak.updateToken(30);
          setToken(keycloak.token || null);
        } catch {}
        throw new Error("Unauthorized (401).");
      } else {
        const txt = await res.text().catch(() => "");
        throw new Error(`Unexpected ${res.status}: ${txt?.slice(0, 300)}`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ------------ Keycloak init & event wiring ------------
  useEffect(() => {
    console.log("[app] mount");
    let cancelled = false;

    initKeycloak()
      .then(async (auth) => {
        if (cancelled) return;
        console.log("[kc.init] authenticated =", auth);
        setAuthenticated(!!auth);

        if (auth) {
          setToken(keycloak.token || null);
          try {
            const p = await keycloak.loadUserProfile();
            setProfile(p);
          } catch {}
          await fetchArticles(Date.now() - DEFAULT_LOOKBACK_MS, { prepend: true });
        }

        keycloak.onAuthSuccess = async () => {
          console.log("[kc] onAuthSuccess");
          setAuthenticated(true);
          setToken(keycloak.token || null);
          try {
            const p = await keycloak.loadUserProfile();
            setProfile(p);
          } catch {}
          await fetchArticles(Date.now() - DEFAULT_LOOKBACK_MS, { prepend: true });
        };
        keycloak.onAuthLogout = () => {
          console.log("[kc] onAuthLogout");
          setAuthenticated(false);
          setProfile(null);
          setToken(null);
          setRows([]);
          setError("");
        };
        keycloak.onAuthRefreshSuccess = () => {
          console.log("[kc] onAuthRefreshSuccess");
          setToken(keycloak.token || null);
        };

        tokenRefreshTimerRef.current = window.setInterval(async () => {
          try {
            const refreshed = await keycloak.updateToken(30);
            if (refreshed) {
              console.log("[kc] proactive token refresh");
              setToken(keycloak.token || null);
            }
          } catch (e) {
            console.warn("proactive token refresh failed", e);
          }
        }, 20 * 1000);
      })
      .catch((e) => {
        console.error("[kc.init] error", e);
      });

    return () => {
      cancelled = true;
      const t = tokenRefreshTimerRef.current;
      if (t) clearInterval(t);
      const r = refreshTimerRef.current;
      if (r) clearInterval(r);
    };
  }, [fetchArticles]);

  // ------------ Auto-refresh every N ms ------------
  useEffect(() => {
    if (!authenticated || !autoRefresh) {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    (async () => {
      await fetchArticles(Date.now() - DEFAULT_LOOKBACK_MS, { prepend: true });
    })();

    refreshTimerRef.current = window.setInterval(async () => {
      await fetchArticles(Date.now() - DEFAULT_LOOKBACK_MS, { prepend: true });
    }, AUTO_REFRESH_MS);

    return () => {
      const r = refreshTimerRef.current;
      if (r) {
        clearInterval(r);
        refreshTimerRef.current = null;
      }
    };
  }, [authenticated, autoRefresh, fetchArticles]);

  // ------------ Actions ------------
  const login = async () => {
    try {
      await keycloak.login({ redirectUri: window.location.href }); // return to same path
    } catch (e) {
      console.error("Login failed", e);
    }
  };

  const logout = async () => {
    try {
      setRows([]);
      setError("");
      setManualDt("");
      const r = refreshTimerRef.current;
      if (r) clearInterval(r);
      const t = tokenRefreshTimerRef.current;
      if (t) clearInterval(t);
      await keycloak.logout({ redirectUri: window.location.origin });
      setAuthenticated(false);
      setProfile(null);
      setToken(null);
    } catch (e) {
      console.error("Logout failed", e);
    }
  };

  const exportCsv = () => {
    if (!displayRows.length) return;
    const headers = Array.from(
      displayRows.reduce((set, row) => {
        Object.keys(row || {}).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );
    const escape = (v) => {
      if (v == null) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [headers.join(",")];
    for (const row of displayRows) lines.push(headers.map((h) => escape(row?.[h])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `articles_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ------------ UI ------------
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-6xl mx-auto p-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Articles Viewer</h1>
          <div className="flex items-center gap-3">
            {authenticated && profile && (
              <div className="text-sm text-slate-600">
                <div className="font-medium">
                  {profile.username || profile.email || profile.firstName || "User"}
                </div>
              </div>
            )}
            {!authenticated ? (
              <button
                onClick={login}
                className="px-4 py-2 rounded-2xl shadow bg-blue-600 text-white hover:bg-blue-700"
              >
                Login
              </button>
            ) : (
              <button
                onClick={logout}
                className="px-4 py-2 rounded-2xl shadow bg-slate-800 text-white hover:bg-black"
              >
                Logout
              </button>
            )}
          </div>
        </header>

        {/* Controls */}
        <section className="mt-6 grid gap-4 md:grid-cols-2 items-end">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={autoRefresh}
                disabled={!authenticated}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span className="text-sm">Auto refresh every 5 minutes</span>
            </label>
            <button
              onClick={exportCsv}
              disabled={!displayRows.length}
              className="px-3 py-2 rounded-xl border bg-white hover:bg-slate-100 disabled:opacity-40"
            >
              Export CSV
            </button>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <input
              type="datetime-local"
              className="px-3 py-2 rounded-xl border bg-white disabled:opacity-40"
              value={manualDt}
              disabled={!authenticated}
              onChange={(e) => setManualDt(e.target.value)}
            />
            <button
              onClick={() => {
                if (!authenticated) return;
                const dt = manualDt ? new Date(manualDt) : new Date(Date.now() - DEFAULT_LOOKBACK_MS);
                if (!Number.isNaN(dt.getTime())) fetchArticles(dt.getTime(), { prepend: true });
              }}
              disabled={!authenticated}
              className="px-4 py-2 rounded-2xl shadow bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              Fetch
            </button>
          </div>
        </section>

        {/* Status */}
        <section className="mt-4">
          {loading && <div className="text-sm text-slate-600">Loading…</div>}
          {!!error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>
          )}
        </section>

        {/* Table */}
        <section className="mt-6 bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  {tableHeaders.length ? (
                    tableHeaders.map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 border-b"
                      >
                        {h}
                      </th>
                    ))
                  ) : (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 border-b">
                      No data
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`transition-colors ${idx % 2 ? "bg-white" : "bg-slate-50"} hover:bg-amber-50`}
                  >
                    {tableHeaders.map((h) => (
                      <td key={h} className="px-4 py-3 align-top text-sm border-b border-slate-100">
                        {formatCell(row?.[h], h)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!displayRows.length && (
            <div className="p-6 text-sm text-slate-500">
              No records yet. {authenticated ? "Use Fetch above or wait for auto refresh." : "Please login to begin."}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
